import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";

// Types
interface Wireframe {
  id: string;
  title: string;
  timestamp: number; // in seconds
  imageUrl: string;
  version: string;
  track: number;
  description?: string;
}

interface Track {
  id: string;
  name: string;
  color: string;
  wireframes: Wireframe[];
}

interface WireframeTimelineProps {
  tracks: Track[];
  totalDuration?: number;
}

const WireframeTimeline: React.FC<WireframeTimelineProps> = ({ 
  tracks, 
  totalDuration = 12 
}) => {
  // Get all wireframes sorted by timestamp
  const allWireframes = useMemo(() => {
    return tracks
      .flatMap((track) => track.wireframes)
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [tracks]);

  // Initialize at timestamp 0 (first wireframe)
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeWireframeId, setActiveWireframeId] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track when component is in view
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    margin: "-100px",
  });

  const pixelsPerSecond = 80;
  const timelineWidth = totalDuration * pixelsPerSecond + 400;
  const timelineHeight = 180;

  // Find active wireframe based on current time
  const activeWireframe = useMemo(() => {
    if (activeWireframeId) {
      return allWireframes.find((w) => w.id === activeWireframeId) || null;
    }

    const activeWireframes = allWireframes.filter((w) => w.timestamp <= currentTime);
    return activeWireframes.length > 0
      ? activeWireframes[activeWireframes.length - 1]
      : null;
  }, [allWireframes, currentTime, activeWireframeId]);

  // Auto-start playback when scrolled into view
  useEffect(() => {
    if (isInView && !isPlaying && currentTime === 0) {
      setIsPlaying(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  // Playback loop
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          const newTime = prev + 0.1;
          if (newTime >= totalDuration) {
            setIsPlaying(false);
            return totalDuration;
          }
          return newTime;
        });
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, totalDuration]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    // Clear active wireframe when playing to allow time-based selection
    if (!isPlaying) {
      setActiveWireframeId(null);
    }
  };

  const handleSeek = (time: number) => {
    setCurrentTime(time);
    setActiveWireframeId(null);
  };

  const handleSkipBack = () => {
    setActiveWireframeId(null);
    const currentIndex = allWireframes.findIndex((w) => w.timestamp >= currentTime);
    const prevIndex = Math.max(0, currentIndex - 1);
    setCurrentTime(allWireframes[prevIndex]?.timestamp || 0);
  };

  const handleSkipForward = () => {
    setActiveWireframeId(null);
    const nextWireframe = allWireframes.find((w) => w.timestamp > currentTime);
    setCurrentTime(nextWireframe?.timestamp || totalDuration);
  };

  const handleWireframeClick = (wireframeId: string) => {
    setActiveWireframeId(wireframeId);
    const wireframe = allWireframes.find((w) => w.id === wireframeId);
    if (wireframe) {
      setCurrentTime(wireframe.timestamp);
    }
  };

  // Playhead dragging logic
  const [isDragging, setIsDragging] = useState(false);
  const playheadRef = useRef<HTMLDivElement>(null);
  const playheadLeft = currentTime * pixelsPerSecond + 176 + 4;

  const handlePlayheadMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const dragStartX = e.clientX;
      const dragStartTime = currentTime;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - dragStartX;
        const deltaTime = deltaX / pixelsPerSecond;
        const newTime = Math.max(0, Math.min(totalDuration, dragStartTime + deltaTime));
        handleSeek(newTime);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [currentTime, pixelsPerSecond, totalDuration]
  );

  // Helper function to get wireframe item colors
  const getColorClasses = (color: string) => {
    switch (color) {
      case "#10B981":
        return { bg: "bg-emerald-500", border: "border-emerald-300" };
      case "#0EA5E9":
        return { bg: "bg-sky-500", border: "border-sky-300" };
      case "#A855F7":
        return { bg: "bg-purple-500", border: "border-purple-300" };
      default:
        return { bg: "bg-emerald-500", border: "border-emerald-300" };
    }
  };

  return (
    <div ref={containerRef} className="p-4 md:p-8 rounded-lg md:rounded-2xl relative bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 w-full">
      {/* Viewer */}
      <div className="p-4">
        {!activeWireframe ? (
          <div className="h-96 bg-zinc-800/30 rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center">
            <div className="text-center text-zinc-400">
              <div className="w-16 h-16 mx-auto mb-4 bg-zinc-800 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p>Select a wireframe from the timeline</p>
            </div>
          </div>
        ) : (
          <motion.div
            key={activeWireframe.id}
            className="bg-zinc-900 rounded-lg border border-zinc-700 shadow-sm overflow-hidden w-fit mx-auto"
          >
            <div className="bg-zinc-800 flex items-center justify-center relative overflow-hidden">
              <img
                src={activeWireframe.imageUrl}
                alt={activeWireframe.title}
                className="max-w-full h-auto"
                style={{ maxHeight: "600px" }}
              />
              <div className="absolute inset-0 bg-black/5 pointer-events-none" />
            </div>
          </motion.div>
        )}
      </div>

      {/* Timeline */}
      <div className="flex flex-col">
        <div className="flex-1 overflow-x-auto overflow-y-hidden max-w-full min-w-0">
          <div className="relative p-4 mx-auto" style={{ width: timelineWidth }}>
            <div className="flex flex-col gap-[2px] pt-4">
              {tracks.map((track) => (
                <div
                  key={track.id}
                  className="h-[41px] relative rounded-lg shrink-0 w-full mb-[2px] bg-zinc-800"
                >
                  {/* Version label */}
                  <p className="absolute font-mono left-[20px] top-[10px] text-[14px] text-zinc-400 w-[148px]">
                    {track.name}
                  </p>

                  {/* Track content area */}
                  <div className="absolute inset-0">
                    {track.wireframes.map((wireframe) => {
                      const left = wireframe.timestamp * pixelsPerSecond + 165;
                      const width = wireframe.title.includes("Early sketches")
                        ? 199
                        : wireframe.title.includes("Lo-fi")
                        ? 464
                        : 428;
                      const colors = getColorClasses(track.color);
                      const isActive = activeWireframeId === wireframe.id;

                      return (
                        <motion.div
                          key={wireframe.id}
                          style={{ left, width }}
                          className={`absolute ${colors.bg} h-[41px] rounded-md top-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200 ${
                            isActive ? "ring-1 ring-white/20" : "border hover:border-white/75"
                          }`}
                          onClick={() => handleWireframeClick(wireframe.id)}
                        >
                          <div className="box-border flex flex-col gap-[10px] h-[41px] items-start overflow-clip p-[4px] relative rounded-md w-full">
                            <div className="flex gap-[12px] items-start relative shrink-0 w-full">
                              {/* Thumbnail */}
                              <div className="relative rounded shrink-0 size-[33px]">
                                <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-md">
                                  <img
                                    alt={wireframe.title}
                                    className="absolute h-full w-full object-cover"
                                    src={wireframe.imageUrl}
                                  />
                                </div>
                              </div>
                              {/* Text */}
                              <p className="font-mono leading-normal overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-zinc-100 text-nowrap whitespace-pre">
                                {wireframe.title}
                              </p>
                            </div>
                          </div>
                          <div className={`absolute border ${colors.border} border-solid inset-0 pointer-events-none rounded-md`} />
                          {isActive && (
                            <motion.div
                              layoutId="active-wireframe"
                              className="absolute inset-0 bg-white/20 rounded-md"
                              initial={false}
                              transition={{ duration: 0.2 }}
                            />
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Playhead */}
            <motion.div
              ref={playheadRef}
              className={`absolute top-0 z-20 ${isDragging ? "cursor-grabbing" : "cursor-grab"} pointer-events-auto`}
              style={{ left: playheadLeft, height: timelineHeight }}
              animate={{ left: isDragging ? undefined : playheadLeft }}
              transition={{ duration: isDragging ? 0 : 0.1, ease: "linear" }}
              onMouseDown={handlePlayheadMouseDown}
            >
              <div
                className={`w-px bg-white shadow-sm transition-all ${
                  isDragging ? "scale-x-[2] bg-white/80" : "hover:scale-x-[2]"
                }`}
                style={{ height: timelineHeight }}
              />
              <div
                className={`absolute top-0 -left-2 w-4 h-6 bg-white rounded-sm shadow-md flex items-center justify-center transition-all ${
                  isDragging ? "scale-110" : "hover:scale-105"
                }`}
              >
                <div className="w-1 h-3 bg-zinc-500 rounded-full" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Controls */}
        <div className="px-4 pt-2 pb-4 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSkipBack}
              className="p-2 border border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700 rounded-lg transition"
            >
              <SkipBack className="w-4 h-4" />
            </button>

            <button
              onClick={handlePlayPause}
              className="p-2 w-12 border border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700 rounded-lg transition"
            >
              {isPlaying ? <Pause className="w-4 h-4 mx-auto" /> : <Play className="w-4 h-4 mx-auto" />}
            </button>

            <button
              onClick={handleSkipForward}
              className="p-2 border border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700 rounded-lg transition"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WireframeTimeline;

